import { useEffect, useRef } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { notifyApiUserAction } from '../../common/apiNotification';
import { notifyAnalysisComplete } from './analysisComplete';
import { ANALYSIS_POLL_MS } from './analysisPollHelpers';
import {
  fetchRecentMyGameAnalyses,
  markMyGameAnalysesNotified,
  type MyGameAnalysisStatusRow,
} from './myGamesApi';
import { MY_GAMES_PATH, myGamePath } from '../shared/gamesBrowseUrl';
import { notifyGameAnalysisComplete } from './gameAnalysisComplete';

function notifyReadyAnalyses(ready: MyGameAnalysisStatusRow[]): void {
  for (const row of ready) {
    notifyGameAnalysisComplete({
      gameId: row.gameId,
      moveQualityCounts: row.moveQualityCounts,
    });
    notifyAnalysisComplete({ gameId: row.gameId });
  }

  if (ready.length === 1) {
    const gameId = ready[0].gameId;
    notifyApiUserAction({
      message: 'Your game analysis is ready.',
      actionLabel: 'Review',
      actionTo: myGamePath(gameId),
    });
    return;
  }

  notifyApiUserAction({
    message: `${ready.length} game analyses are ready.`,
    actionLabel: 'View games',
    actionTo: MY_GAMES_PATH,
  });
}

export function usePendingGameAnalysisPoll(): void {
  const { isLoggedIn } = useAuth();
  const pollInFlightRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const poll = async () => {
      if (pollInFlightRef.current) {
        return;
      }
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }

      pollInFlightRef.current = true;
      try {
        const { games } = await fetchRecentMyGameAnalyses();
        const ready = games.filter((game) => game.analysisStatus === 'ready');

        if (ready.length > 0) {
          notifyReadyAnalyses(ready);
          void markMyGameAnalysesNotified(ready.map((game) => game.gameId)).catch(
            () => undefined,
          );
        }
      } catch {
        /* keep polling on the base interval */
      } finally {
        pollInFlightRef.current = false;
      }
    };

    void poll();

    const pollId = window.setInterval(() => {
      void poll();
    }, ANALYSIS_POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void poll();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(pollId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isLoggedIn]);
}
